// Globals
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  changeOrderManager,
  changeOrderStatus,
  deleteOrder,
  getOrderDetails,
  ordersSelector,
  recordAdditionalPayment,
  recordRefund,
  issueOrderReturn,
  updateOrderDelivery,
  updateOrderPayment,
  updateOrderPaymentChannel,
  updateOrderClient,
  changeOrderRegion,
} from '../../feature';

// Models
import {
  OrderCancellationReason,
  OrderPaymentChannel,
  OrdersDeliveryType,
  OrdersPaymentStatus,
  OrdersStatus,
} from '@/models/orders.model';
import { IApiError } from '@/models/apiError.model';
import { IIssueOrderReturnItem } from '@/models/orders.model';
import { IManager, Roles } from '@/models/admins.model';
import { ROUTES } from '@/models/router.model';

// Modules
import { useAutocompleteRegions } from '@/modules/Regions/hooks';
import { Resource, usePermissions } from '@/modules/Permissions';
import { selectAdminProfile } from '@/modules/AdminProfile';

// Components
import { showErrorToast, showSuccessToast, useForm, FormItem } from '@/components';
import {
  StyledContainerWrapper,
  StyledDeliveryErrorWrapper,
  StyledDeliveryErrorText,
} from './OrderAdditionalInfoContainerStyles';
import {
  AdditionalInfoItemWrapper,
  AdditionalInfoContentBlock,
  UpdateOrderPaymentModal,
  UpdateOrderStatusModal,
  UpdateOrderClientModal,
  UpdateOrderDeliveryModal,
  DeliveryUpdatedModal,
  PaymentInfoContentBlock,
  FiscalReceiptsBlock,
  StatusHistoryBlock,
  CompositionHistoryBlock,
  OrderStatusBadge,
  DeleteOrderModal,
  RecordAdditionalPaymentModal,
  RecordRefundModal,
  FiscalReturnModal,
} from '../../components';
import { Button, Select } from '@/ui';
import { Modal } from 'antd';

// Helpers
import {
  getDeliveryFormValues,
  getOrderClientData,
  getOrderDeliveryData,
  getClientFormValues,
  getOrderResultsData,
  getOrderUtmAttributionData,
  paymentEditStatuses,
  getRegionSelectConfig,
  isOrderContentEditable,
  getPaymentsInfo,
} from '../../helpers';
import { useModal } from '@/hooks';
import { UpdateDeliveryDTO, UpdateClientDTO } from '../../dtos';
import { formatDate } from '@/utils';
import { DateFormats } from '@/constants/dates';

const OrderAdditionalInfoContainer = () => {
  const { t } = useTranslation(['orders']);
  const navigate = useNavigate();
  const { order, isLoading, managers } = useAppState(ordersSelector);
  const profile = useAppState(selectAdminProfile);
  const { canManage, can } = usePermissions();
  const canManageOrders = canManage(Resource.ORDER);
  const isLogistician = profile?.role === Roles.LOGISTICIAN;
  const canManageFullOrder = canManageOrders && !isLogistician;
  // Deleting an order is a dedicated, higher-privilege action (order.delete), separate from
  // regular order processing (order.manage) — currently only super_admin holds it.
  const canDeleteOrder = can('order.delete');
  const [form] = useForm();
  const [updateClientForm] = useForm();
  const dispatch = useAppDispatch();
  const [regionId, setRegionId] = useState<string | null>(null);
  const [pendingManager, setPendingManager] = useState<IManager | null>(null);
  const recordingPaymentRef = useRef(false);
  const managersOptions = useMemo(() => managers.map((m) => ({ label: m.full_name, value: m.id })), [managers]);
  // A legacy responsible admin outside the assignable roster must not appear among the
  // options: the select falls back to the placeholder (the current manager's name is
  // already shown in the block above), and only roster members can be picked.
  const selectedManagerId = managers.some((m) => m.id === order?.manager?.id) ? order?.manager?.id : undefined;
  const managerRegionId = managers.find((m) => m.id === order?.manager?.id)?.region_id ?? null;
  const deliveryInitialValues = getDeliveryFormValues(order);
  const clientInitialValues = getClientFormValues(order);
  const isStatusChangeEnabled = canManageOrders && paymentEditStatuses?.has(order?.status as OrdersStatus);
  const deliveryType = order?.delivery?.delivery as OrdersDeliveryType | undefined;
  const isContentEditable = canManageFullOrder && isOrderContentEditable(order?.status, deliveryType, profile?.role);
  const isPaymentEditable = canManageFullOrder && isStatusChangeEnabled;
  const { onSearch: onSearchRegions, regionsOptions } = useAutocompleteRegions('', 100);
  const resolvedRegionId =
    order?.region?.id ??
    (managerRegionId && regionsOptions?.some((o) => o.value === managerRegionId) ? managerRegionId : null);
  const paymentsInfo = getPaymentsInfo(order?.payments_info ?? []);
  const totalPaymentBalance = paymentsInfo.reduce((sum, item) => sum + (item.paymentBalance || 0), 0);
  const totalRefundAmount = paymentsInfo.reduce((sum, item) => sum + (item.refundAmount || 0), 0);
  const showAdditionalPaymentButton =
    canManageFullOrder &&
    (order?.payment_status === OrdersPaymentStatus.AWAITING_ADDITIONAL_PAYMENT || totalPaymentBalance > 0);
  const showRefundButton =
    canManageFullOrder &&
    !order?.refund_date &&
    (order?.payment_status === OrdersPaymentStatus.REQUIRES_PARTIAL_REFUND ||
      order?.payment_status === OrdersPaymentStatus.PAYMENT_REFUND ||
      order?.payment_status === OrdersPaymentStatus.PREPAYMENT_REFUND ||
      totalRefundAmount > 0);

  const {
    isOpened: isOpenUpdateOrderPaymentModal,
    openModal: openUpdateOrderPaymentModal,
    closeModal: closeUpdateOrderPaymentModal,
  } = useModal();
  const {
    isOpened: isOpenUpdateOrderDeliveryModal,
    openModal: openUpdateOrderDeliveryModal,
    closeModal: closeUpdateOrderDeliveryModal,
  } = useModal();
  const {
    isOpened: isOpenDeliveryUpdatedModal,
    openModal: openDeliveryUpdatedModal,
    closeModal: closeDeliveryUpdatedModal,
  } = useModal();
  const {
    isOpened: isOpenUpdateClientModal,
    openModal: openUpdateClientModal,
    closeModal: closeUpdateClientModal,
  } = useModal();
  const {
    isOpened: isOpenUpdateOrderStatusModal,
    openModal: openUpdateOrderStatusModal,
    closeModal: closeUpdateOrderStatusModal,
  } = useModal();
  const {
    isOpened: isOpenAdditionalPaymentModal,
    openModal: openAdditionalPaymentModal,
    closeModal: closeAdditionalPaymentModal,
  } = useModal();
  const { isOpened: isOpenRefundModal, openModal: openRefundModal, closeModal: closeRefundModal } = useModal();
  const { isOpened: isOpenReturnModal, openModal: openReturnModal, closeModal: closeReturnModal } = useModal();
  const {
    isOpened: isOpenDeleteOrderModal,
    openModal: openDeleteOrderModal,
    closeModal: closeDeleteOrderModal,
  } = useModal();

  const clientData = getOrderClientData(t, order);
  const deliveryData = getOrderDeliveryData(t, order, order?.delivery?.settlement_label || order?.delivery?.settlement);
  const resultsData = getOrderResultsData(t, order);
  const utmData = getOrderUtmAttributionData(t, order);

  const handleUpdatePayment = async (paymentStatus?: OrdersPaymentStatus, paymentChannel?: OrderPaymentChannel) => {
    const orderId = order?.id as string;
    const requests: Promise<unknown>[] = [];

    if (paymentStatus && paymentStatus !== order?.payment_status) {
      requests.push(dispatch(updateOrderPayment({ order_id: orderId, payment_status: paymentStatus })).unwrap());
    }

    if (paymentChannel && paymentChannel !== order?.payment_channel) {
      requests.push(
        dispatch(updateOrderPaymentChannel({ order_id: orderId, payment_channel: paymentChannel })).unwrap(),
      );
    }

    if (requests.length > 0) {
      await Promise.all(requests);
      showSuccessToast({ message: t('order_data_saved') });
    }

    closeUpdateOrderPaymentModal();
  };

  const handleUpdateStatus = async (
    status?: OrdersStatus,
    cancellationReason?: OrderCancellationReason,
    cancellationReasonText?: string,
  ) => {
    if (!status || status === order?.status) {
      closeUpdateOrderStatusModal();
      return;
    }

    try {
      await dispatch(
        changeOrderStatus({
          order_id: order?.id as string,
          status,
          cancellation_reason: cancellationReason,
          cancellation_reason_text: cancellationReasonText,
        }),
      ).unwrap();
      await dispatch(getOrderDetails(order?.id as string)).unwrap();
      showSuccessToast({ message: t('order_data_saved') });
      closeUpdateOrderStatusModal();
    } catch (error) {}
  };

  const handleChangeCityButton = () => {
    closeDeliveryUpdatedModal();
  };

  const handleUpdateDelivery = async () => {
    try {
      const values = form.getFieldsValue();
      const requestDto = new UpdateDeliveryDTO({
        order_id: order?.id,
        region_id: regionId ?? order?.delivery?.region_id,
        ...values,
        address: values?.address?.name || values?.address,
      });

      await dispatch(updateOrderDelivery(requestDto)).unwrap();
      const updatedOrder = await dispatch(getOrderDetails(order?.id as string)).unwrap();
      const newFormValues = getDeliveryFormValues(updatedOrder.data);
      if (newFormValues) {
        form.setFieldsValue(newFormValues);
      }
      showSuccessToast({ message: t('order_data_saved') });
      closeUpdateOrderDeliveryModal();
      if (deliveryInitialValues?.settlement_id !== newFormValues?.settlement_id) {
        openDeliveryUpdatedModal();
      }
    } catch (error) {}
  };

  const handleUpdateOrderClient = async () => {
    try {
      const values = updateClientForm.getFieldsValue();
      const requestDto = new UpdateClientDTO(values);

      await dispatch(updateOrderClient({ order_id: order?.id as string, data: requestDto })).unwrap();
      showSuccessToast({ message: t('order_data_saved') });
      closeUpdateClientModal();
    } catch (error) {}
  };

  const handleSelectUnit = async (value: string) => {
    try {
      await dispatch(changeOrderRegion({ order_id: order?.id as string, region_id: value })).unwrap();
      showSuccessToast({ message: t('order_data_saved') });
      dispatch(getOrderDetails(order?.id as string));
    } catch (error) {}
  };

  const handleManagerSelectChange = (managerId: string) => {
    setPendingManager(managers.find((m) => m.id === managerId) ?? null);
  };

  const handleManagerChangeConfirm = async () => {
    if (!pendingManager) return;
    try {
      const orderId = order?.id as string;
      await dispatch(changeOrderManager({ order_id: orderId, manager_id: pendingManager.id })).unwrap();
      if (pendingManager.region_id) {
        await dispatch(changeOrderRegion({ order_id: orderId, region_id: pendingManager.region_id })).unwrap();
      }
      await dispatch(getOrderDetails(orderId)).unwrap();
      showSuccessToast({ message: t('order_data_saved') });
    } catch (error) {
    } finally {
      setPendingManager(null);
    }
  };

  const handleManagerChangeCancel = () => {
    setPendingManager(null);
  };

  const handleDeleteOrder = async () => {
    if (!order?.id) return;

    try {
      await dispatch(deleteOrder(order.id)).unwrap();
      showSuccessToast({ message: t('order_delete_success') });
      closeDeleteOrderModal();
      navigate(ROUTES.ORDERS);
    } catch (error) {}
  };

  const handleRecordAdditionalPayment = async (payload: { amount: number; payment_channel: OrderPaymentChannel }) => {
    if (!order?.id || recordingPaymentRef.current) return;
    recordingPaymentRef.current = true;

    try {
      await dispatch(recordAdditionalPayment({ order_id: order.id, ...payload })).unwrap();
      await dispatch(getOrderDetails(order.id)).unwrap();
      showSuccessToast({ message: t('order_data_saved') });
      closeAdditionalPaymentModal();
    } catch (error) {
      const apiError = error as IApiError;
      showErrorToast({ message: apiError?.message || t('order_status_change_error') });
    } finally {
      recordingPaymentRef.current = false;
    }
  };

  const handleRecordRefund = async (payload: { amount: number }) => {
    if (!order?.id) return;

    try {
      await dispatch(recordRefund({ order_id: order.id, ...payload })).unwrap();
      await dispatch(getOrderDetails(order.id)).unwrap();
      showSuccessToast({ message: t('order_data_saved') });
      closeRefundModal();
    } catch (error) {
      const apiError = error as IApiError;
      showErrorToast({ message: apiError?.message || t('order_status_change_error') });
    }
  };

  const handleIssueReturn = async (payload: { all?: boolean; items?: IIssueOrderReturnItem[] }) => {
    if (!order?.id) return;

    try {
      await dispatch(issueOrderReturn({ order_id: order.id, ...payload })).unwrap();
      await dispatch(getOrderDetails(order.id)).unwrap();
      showSuccessToast({ message: t('fiscal_return_success') });
      closeReturnModal();
    } catch (error) {
      const apiError = error as IApiError;
      showErrorToast({ message: apiError?.message || t('order_status_change_error') });
    }
  };

  const hasReturnableReceipts = !!order?.fiscal_receipts?.some(
    (receipt) => receipt.type === 'sell' && receipt.status === 'done',
  );

  return (
    <StyledContainerWrapper>
      {canManageFullOrder && (
        <AdditionalInfoItemWrapper title={t('manager_and_branch')}>
          <FormItem
            label={t('order_details_manager')}
            htmlFor="order-manager-select"
            labelCol={{ span: 24, style: { height: 'auto' } }}
          >
            <Select
              id="order-manager-select"
              options={managersOptions}
              placeholder={t('order_manager_select_placeholder')}
              onChange={handleManagerSelectChange}
              value={selectedManagerId}
              size="middle"
              style={{ width: '100%' }}
            />
          </FormItem>
          {order?.status !== OrdersStatus.NEW && (
            <FormItem
              label={t('order_details_unti')}
              htmlFor="order-region-select"
              labelCol={{ span: 24, style: { height: 'auto' } }}
              style={{ marginTop: 15 }}
            >
              <Select
                id="order-region-select"
                options={getRegionSelectConfig(regionsOptions, order?.region) ?? []}
                size="middle"
                filterOption={false}
                onSearch={onSearchRegions}
                value={resolvedRegionId ?? ''}
                allowClear
                showSearch
                disabled={
                  !isContentEditable &&
                  !(order?.status === OrdersStatus.PROCESSING && !(order?.region?.id || order?.region_id))
                }
                onChange={handleSelectUnit}
                placeholder={t('oreder_search_region_placeholder')}
              />
            </FormItem>
          )}
        </AdditionalInfoItemWrapper>
      )}
      {!canManageFullOrder && order?.manager && (
        <AdditionalInfoItemWrapper title={t('order_details_manager')}>
          <AdditionalInfoContentBlock label={t('order_details_current_manager')} value={order.manager.full_name} />
        </AdditionalInfoItemWrapper>
      )}
      <Modal
        open={!!pendingManager}
        title={t('order_change_manager_modal_title')}
        onOk={handleManagerChangeConfirm}
        onCancel={handleManagerChangeCancel}
        confirmLoading={isLoading}
        okText={t('order_change_manager_modal_confirm')}
        cancelText={t('order_change_manager_modal_cancel')}
      >
        <p>{t('order_change_manager_modal_body', { name: pendingManager?.full_name })}</p>
      </Modal>
      {canManageOrders && order && (
        <AdditionalInfoItemWrapper
          title={t('order_details_status')}
          handleEditButton={openUpdateOrderStatusModal}
          isEditable
        >
          <OrderStatusBadge t={t} record={order} wrap />
          {order.edited_after_fulfillment && (
            <AdditionalInfoContentBlock
              label={t('orders_edited_after_fulfillment_badge')}
              value={t('orders_edited_after_fulfillment_badge')}
            />
          )}
        </AdditionalInfoItemWrapper>
      )}
      {canDeleteOrder && order && (
        <Button type="text" danger onClick={openDeleteOrderModal}>
          {t('order_delete_btn')}
        </Button>
      )}
      {order?.status_history && (
        <StatusHistoryBlock t={t} statusHistory={order.status_history} currentStatus={order.status} />
      )}
      <CompositionHistoryBlock logs={order?.composition_change_logs} />
      <AdditionalInfoItemWrapper
        title={t('order_details_client')}
        handleEditButton={openUpdateClientModal}
        isEditable={isContentEditable}
      >
        {clientData.map((item) => (
          <AdditionalInfoContentBlock key={item.label} label={item.label} value={item.value} />
        ))}
      </AdditionalInfoItemWrapper>
      <AdditionalInfoItemWrapper
        title={t('order_details_delivery')}
        handleEditButton={openUpdateOrderDeliveryModal}
        isEditable={isContentEditable}
      >
        {order?.delivery?.error ? (
          <StyledDeliveryErrorWrapper>
            <StyledDeliveryErrorText>{order?.delivery?.error_message}</StyledDeliveryErrorText>
            {isContentEditable && (
              <Button type="default" onClick={openUpdateOrderDeliveryModal}>
                {t('order_delivery_error_btn')}
              </Button>
            )}
          </StyledDeliveryErrorWrapper>
        ) : (
          deliveryData.map((item) => (
            <AdditionalInfoContentBlock key={item.label} label={item.label} value={item.value} />
          ))
        )}
      </AdditionalInfoItemWrapper>
      <AdditionalInfoItemWrapper
        title={t('order_details_payment')}
        handleEditButton={openUpdateOrderPaymentModal}
        isEditable={isPaymentEditable}
      >
        <PaymentInfoContentBlock data={order} />
        {order?.refund_date && (
          <AdditionalInfoContentBlock
            label={t('orders_list_refund_date')}
            value={formatDate(order.refund_date, DateFormats.EUROPEAN)}
          />
        )}
        {(showAdditionalPaymentButton || showRefundButton) && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {showAdditionalPaymentButton && (
              <Button type="primary" onClick={openAdditionalPaymentModal}>
                {t('orders_record_additional_payment')}
              </Button>
            )}
            {showRefundButton && <Button onClick={openRefundModal}>{t('orders_record_refund')}</Button>}
          </div>
        )}
      </AdditionalInfoItemWrapper>
      <AdditionalInfoItemWrapper title={t('fiscal_receipts_title')}>
        <FiscalReceiptsBlock receipts={order?.fiscal_receipts} />
        {canManageFullOrder && hasReturnableReceipts && (
          <Button type="default" style={{ marginTop: 12 }} onClick={openReturnModal}>
            {t('fiscal_return_button')}
          </Button>
        )}
      </AdditionalInfoItemWrapper>
      <AdditionalInfoItemWrapper title={t('order_details_result')}>
        {resultsData.map((item) => (
          <AdditionalInfoContentBlock key={item.label} label={item.label} value={item.value} />
        ))}
      </AdditionalInfoItemWrapper>
      {order && canManageFullOrder && (
        <UpdateOrderClientModal
          isLoading={isLoading}
          isOpenUpdateOrderClientModal={isOpenUpdateClientModal}
          closeUpdateOrderClientModal={closeUpdateClientModal}
          handleUpdateOrderClient={handleUpdateOrderClient}
          formInstance={updateClientForm}
          initialValues={clientInitialValues}
        />
      )}
      {order && canManageFullOrder && (
        <UpdateOrderPaymentModal
          isLoading={isLoading}
          isOpenUpdateOrderPaymentModal={isOpenUpdateOrderPaymentModal}
          handleUpdatePayment={handleUpdatePayment}
          closeUpdateOrderPaymentModal={closeUpdateOrderPaymentModal}
          currentPaymentStatus={order.payment_status}
          currentPaymentChannel={order.payment_channel}
          orderStatus={order.status}
        />
      )}
      {order && canManageOrders && (
        <UpdateOrderStatusModal
          isLoading={isLoading}
          isOpenUpdateOrderStatusModal={isOpenUpdateOrderStatusModal}
          handleUpdateStatus={handleUpdateStatus}
          closeUpdateOrderStatusModal={closeUpdateOrderStatusModal}
          currentStatus={order.status}
          deliveryType={deliveryType}
        />
      )}
      {order?.delivery && canManageFullOrder && (
        <UpdateOrderDeliveryModal
          isOpenUpdateOrderDeliveryModal={isOpenUpdateOrderDeliveryModal}
          closeUpdateOrderDeliveryModal={closeUpdateOrderDeliveryModal}
          handleSubmit={handleUpdateDelivery}
          formInstance={form}
          order={order}
          initialValues={deliveryInitialValues}
          setRegionId={setRegionId}
        />
      )}
      {order && (
        <DeliveryUpdatedModal
          isOpen={isOpenDeliveryUpdatedModal}
          closeModal={closeDeliveryUpdatedModal}
          onChangeCity={handleChangeCityButton}
          settlementName={order.delivery?.settlement ?? ''}
        />
      )}
      <AdditionalInfoItemWrapper title={t('order_details_utm_section')}>
        {utmData.map((item) => (
          <AdditionalInfoContentBlock key={item.label} label={item.label} value={item.value} />
        ))}
      </AdditionalInfoItemWrapper>
      <DeleteOrderModal
        isOpen={isOpenDeleteOrderModal}
        isLoading={isLoading}
        onClose={closeDeleteOrderModal}
        onConfirm={handleDeleteOrder}
      />
      <RecordAdditionalPaymentModal
        isOpen={isOpenAdditionalPaymentModal}
        isLoading={isLoading}
        defaultAmount={totalPaymentBalance}
        onClose={closeAdditionalPaymentModal}
        onSubmit={handleRecordAdditionalPayment}
      />
      <RecordRefundModal
        isOpen={isOpenRefundModal}
        isLoading={isLoading}
        defaultAmount={totalRefundAmount}
        onClose={closeRefundModal}
        onSubmit={handleRecordRefund}
      />
      <FiscalReturnModal
        isOpen={isOpenReturnModal}
        isLoading={isLoading}
        receipts={order?.fiscal_receipts}
        onClose={closeReturnModal}
        onSubmit={handleIssueReturn}
      />
    </StyledContainerWrapper>
  );
};
export default OrderAdditionalInfoContainer;
